"use client";
import {
    IAccountAssets,
    IBlocks,
    IBlog,
    IBlogPost,
    ITheme,
    IUserData,
} from "@/types";
import React from "react";
import { BlockUpdateFunction } from ".";
import AdminPopover from "../../content/AdminPopover";
import BlockToggle from "./tabs/toggle";
import Options from "./tabs/blogPostSingle/options";
import PostList from "../../blog/PostList";
import { BlogPostSkeletonBlock } from "../../blog/skeleton";
import { Separator } from "@/components/ui/separator";
import BlockMetadata from "./tabs/metadata";
interface Props {
    admin?: boolean;
    user: IUserData;
    block: IBlocks;
    update: BlockUpdateFunction;
    style: any;
    assets: IAccountAssets[];
    theme?: IBlog["theme"];
    newBlock?: string | null;
    posts: IBlogPost[];
    accountTheme: ITheme;
}
export default function BlockBlogPostSingle({
    admin,
    user,
    block,
    update,
    style,
    assets,
    theme,
    newBlock,
    posts,
    accountTheme,
}: Props) {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const [blog, setBlog] = React.useState<IBlog | null>(null);
    const [post, setPost] = React.useState<IBlogPost | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    React.useEffect(() => {
        if (newBlock === block.id) {
            handleExpand();
        }
        return () => {
            window.removeEventListener("closeBlock", handleClose);
        };
    }, [newBlock, block.id]);
    React.useEffect(() => {
        window.addEventListener("closeBlock", {
            handleEvent: (event: CustomEvent) => {
                if (event.detail.id != block.id) {
                    if (isExpanded) {
                        setIsExpanded(false);
                    }
                }
            },
        });
    });
    React.useEffect(() => {
        if (block != blockUpdate) {
            setBlockUpdate(block);
        }
    }, [block]);
    const handleClose = () => {
        setIsExpanded(false);
        window.dispatchEvent(new CustomEvent("collapseBlock"));
    };
    function handleUpdate(value: IBlocks) {
        if (value == null) {
            update(blockUpdate.id, value, true);
            return;
        }
        setBlockUpdate({
            ...blockUpdate,
            ...value,
        });
        if (block == value) return;
        update(blockUpdate.id, value);
    }
    const event = new CustomEvent("expandBlock", {
        detail: {
            id: blockUpdate.id,
            title: "Blog Post Single",
            description:
                "A block that displays a single blog post from a blog.",
            titleOptions: (
                <BlockToggle
                    block={blockUpdate}
                    blockUpdate={setBlockUpdate}
                    update={handleUpdate}
                />
            ),
            content: (
                <div className="flex flex-col gap-3 pb-3">
                    <Options
                        key={block.id}
                        account={user}
                        block={blockUpdate}
                        blockUpdate={setBlockUpdate}
                        update={handleUpdate}
                        posts={posts}
                    />
                    <BlockMetadata block={blockUpdate} update={handleUpdate} />
                </div>
            ),
        },
    });
    const handleExpand = () => {
        window.dispatchEvent(
            new CustomEvent("closeBlock", {
                detail: {
                    id: block.id,
                },
            })
        );
        setIsExpanded(true);
        window.dispatchEvent(event);
    };
    React.useEffect(() => {
        if (isExpanded) {
            window.dispatchEvent(event);
        }
    }, [blockUpdate]);
    React.useEffect(() => {
        async function getPost() {
            if (blockUpdate.options?.post_id) {
                const res = await fetch(
                    `/api/account/${user.id}/post/${blockUpdate.options?.post_id}`
                );
                const post = await res.json();
                if (post.data) {
                    setPost(post.data.post);
                    setBlog(post.data.blog);
                    setIsLoading(false);
                }
            }
        }
        getPost();
    }, [blockUpdate.options?.post_id, user.id]);
    return (
        <AdminPopover
            admin={admin}
            className={block.show ? "" : "opacity-30"}
            onClick={handleExpand}
            title={"Blog Post Single"}
            isOpen={isExpanded}
            onClose={handleClose}
            key={block.id}
        >
            <div
                key={block.id}
                className={`flex w-full flex-row flex-wrap gap-2 p-2 ${user.theme.button_rounded} relative`}
                id="BlogPostSingle"
            >
                {isLoading && <BlogPostSkeletonBlock account={user} />}
                {!isLoading && blog && post && (
                    <PostList
                        blog={blog}
                        assets={[]}
                        post={{
                            ...post,
                            category_name:
                                blog.categories.find(
                                    (e) => e.id == post.category
                                )?.name || "",
                        }}
                        theme={theme}
                        account={user}
                        accountTheme={accountTheme}
                    />
                )}
            </div>
        </AdminPopover>
    );
}
